import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Package, Heart, MapPin, Clock, ArrowRight, Star, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountOverview = () => {
  const { profile } = useOutletContext();
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [ordersRes, viewedRes, recsRes] = await Promise.all([
        axios.get(`${API}/account/orders`, { headers }),
        axios.get(`${API}/account/recently-viewed`, { headers }).catch(() => ({ data: { products: [] } })),
        axios.get(`${API}/account/recommendations`, { headers }).catch(() => ({ data: { products: [] } }))
      ]);

      setRecentOrders((ordersRes.data.orders || []).slice(0, 3));
      setRecentlyViewed(viewedRes.data.products || []);
      setRecommendations(recsRes.data.products || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-indigo-100 text-indigo-800',
      packed: 'bg-purple-100 text-purple-800',
      shipped: 'bg-cyan-100 text-cyan-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      returned: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="account-overview">
      {/* Welcome */}
      <div className="bg-white border border-charcoal/10 p-6">
        <h1 className="font-heading text-2xl font-light text-charcoal">
          Welcome back, {profile?.full_name?.split(' ')[0] || 'there'}!
        </h1>
        <p className="text-charcoal-light mt-1">
          Manage your orders, wishlist, and account settings from here.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/account/orders" className="bg-white border border-charcoal/10 p-4 hover:border-gold transition-colors group">
          <Package className="w-8 h-8 text-gold mb-2" />
          <p className="font-heading text-2xl font-light text-charcoal">{profile?.total_orders || 0}</p>
          <p className="text-xs text-charcoal-light">Total Orders</p>
        </Link>
        <Link to="/account/wishlist" className="bg-white border border-charcoal/10 p-4 hover:border-gold transition-colors group">
          <Heart className="w-8 h-8 text-gold mb-2" />
          <p className="font-heading text-2xl font-light text-charcoal">0</p>
          <p className="text-xs text-charcoal-light">Wishlist Items</p>
        </Link>
        <Link to="/account/addresses" className="bg-white border border-charcoal/10 p-4 hover:border-gold transition-colors group">
          <MapPin className="w-8 h-8 text-gold mb-2" />
          <p className="font-heading text-2xl font-light text-charcoal">0</p>
          <p className="text-xs text-charcoal-light">Saved Addresses</p>
        </Link>
        <div className="bg-white border border-charcoal/10 p-4">
          <Star className="w-8 h-8 text-gold mb-2" />
          <p className="font-heading text-2xl font-light text-charcoal">{profile?.loyalty_points || 0}</p>
          <p className="text-xs text-charcoal-light">Loyalty Points</p>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
          <h2 className="font-heading text-lg font-light text-charcoal">Recent Orders</h2>
          <Link to="/account/orders" className="text-sm text-gold hover:underline flex items-center gap-1">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        {recentOrders.length > 0 ? (
          <div className="divide-y divide-charcoal/5">
            {recentOrders.map((order) => (
              <Link
                key={order.id}
                to={`/account/orders/${order.id}`}
                className="flex items-center justify-between p-4 hover:bg-beige/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-beige flex items-center justify-center">
                    {order.items?.[0]?.image ? (
                      <img src={order.items[0].image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-6 h-6 text-charcoal-light" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-charcoal">Order #{order.order_number}</p>
                    <p className="text-sm text-charcoal-light">{formatDate(order.created_at)}</p>
                    <p className="text-sm text-charcoal-light">{order.items?.length || 0} items</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-charcoal">{formatCurrency(order.total_amount)}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs ${getStatusColor(order.order_status)}`}>
                    {order.order_status?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-charcoal-light">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No orders yet</p>
            <Link to="/products" className="text-gold hover:underline text-sm mt-2 inline-block">
              Start Shopping
            </Link>
          </div>
        )}
      </div>

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && (
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10">
            <h2 className="font-heading text-lg font-light text-charcoal flex items-center gap-2">
              <Eye className="w-5 h-5 text-gold" />
              Recently Viewed
            </h2>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {recentlyViewed.slice(0, 4).map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="group"
              >
                <div className="aspect-square bg-beige overflow-hidden mb-2">
                  <img
                    src={product.images?.[0] || ''}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                <p className="text-sm text-charcoal truncate">{product.name}</p>
                <p className="text-sm font-medium text-gold">{formatCurrency(product.discount_price || product.price)}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10">
            <h2 className="font-heading text-lg font-light text-charcoal">Recommended For You</h2>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {recommendations.slice(0, 4).map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="group"
              >
                <div className="aspect-square bg-beige overflow-hidden mb-2">
                  <img
                    src={product.images?.[0] || ''}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                <p className="text-sm text-charcoal truncate">{product.name}</p>
                <p className="text-sm font-medium text-gold">{formatCurrency(product.discount_price || product.price)}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountOverview;
